import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import React, { useCallback, useState } from 'react';
import { useAuthStore } from '../../../../store/storeAuth';
import { useStoreThirdPartyIntegration } from '../../../../store/storeThirdPartyIntegration';
import ConnectToProvider from '../../oauth/ConnectToProvider';
import type { ConnectionCardProps } from '../../types';
import SlackIcon from './SlackIcon';

const SLACK_PURPLE = '#4A154B';
const SLACK_PURPLE_LIGHT = 'rgba(74, 21, 75, 0.08)';

const SlackConnectionCard: React.FC<ConnectionCardProps> = ({
  provider,
  isConnected,
  onReconnect,
  onDisconnect,
  onConnected,
}) => {
  const { user } = useAuthStore(state => state);
  const { fetchConnections, getConnections } = useStoreThirdPartyIntegration();
  const isDark = user?.theme === 'dark';
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const handleOAuthSuccess = useCallback(() => {
    fetchConnections(provider).then(() => {
      const latest = getConnections(provider);
      if (latest.length > 0) {
        const newest = latest[0];
        onConnected?.(newest.id, newest.name);
      }
    });
  }, [provider, fetchConnections, getConnections, onConnected]);

  const cardBorder = isDark ? '1px solid rgba(71, 84, 103, 0.5)' : '1px solid rgba(196, 207, 255, 1)';
  const cardBg = isDark ? 'rgba(74, 21, 75, 0.12)' : SLACK_PURPLE_LIGHT;
  const titleColor = isDark ? '#F9FAFB' : 'rgba(71, 84, 103, 1)';
  const subtitleColor = isDark ? '#98A2B3' : 'rgba(102, 112, 133, 1)';
  const connectColor = '#4A154B';

  const handleDisconnectClick = () => setConfirmOpen(true);
  const handleConfirmClose = () => {
    if (!isDisconnecting) setConfirmOpen(false);
  };
  const handleDisconnectConfirm = async () => {
    if (!onDisconnect || isDisconnecting) return;
    setIsDisconnecting(true);
    try {
      await onDisconnect();
      setConfirmOpen(false);
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (isConnected) {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: 280,
          border: cardBorder,
          backgroundColor: cardBg,
          padding: '16px',
          borderRadius: '8px',
          position: 'relative',
        }}
      >
        {onDisconnect && (
          <>
            <IconButton
              className="nodrag"
              size="small"
              onClick={handleDisconnectClick}
              disabled={isDisconnecting}
              aria-label="Disconnect Slack"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: subtitleColor,
                '&:hover': { color: titleColor, backgroundColor: 'rgba(0,0,0,0.04)' },
              }}
            >
              <X size={16} />
            </IconButton>
            <Dialog
              open={confirmOpen}
              onClose={handleConfirmClose}
              PaperProps={{
                sx: {
                  borderRadius: '8px',
                  bgcolor: isDark ? '#1D2939' : '#fff',
                  color: titleColor,
                },
              }}
            >
              <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>
                Disconnect Slack?
              </DialogTitle>
              <DialogContent>
                <Typography sx={{ color: subtitleColor, fontSize: '14px' }}>
                  Your workspace will be disconnected. You can connect again anytime.
                </Typography>
              </DialogContent>
              <DialogActions sx={{ gap: 1, px: 2, pb: 2 }}>
                <Button
                  onClick={handleConfirmClose}
                  disabled={isDisconnecting}
                  sx={{ textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleDisconnectConfirm}
                  disabled={isDisconnecting}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: '#EF4444',
                    '&:hover': { backgroundColor: '#DC2626' },
                  }}
                >
                  {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              backgroundColor: SLACK_PURPLE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SlackIcon size={20} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: titleColor,
                fontWeight: 600,
                fontSize: '13px',
                lineHeight: 1.3,
              }}
            >
              Slack
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#22C55E',
                }}
              />
              <Typography sx={{ fontSize: '11px', color: subtitleColor }}>
                Connected
              </Typography>
            </Box>
          </Box>
          {onReconnect && (
            <Button
              size="small"
              onClick={onReconnect}
              sx={{
                textTransform: 'none',
                fontSize: '11px',
                color: connectColor,
                fontWeight: 600,
              }}
            >
              Reconnect
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <ConnectToProvider
      provider={provider}
      onSuccess={handleOAuthSuccess}
    >
      {({ onClick, isConnecting }) => (
        <Box
          sx={{
            width: '100%',
            maxWidth: 280,
            border: cardBorder,
            backgroundColor: cardBg,
            padding: '16px',
            borderRadius: '8px',
            position: 'relative',
            transition: 'box-shadow 0.2s ease',
            '&:hover': { boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 12px rgba(74, 21, 75, 0.15)' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              borderBottom: isDark ? '1px solid rgba(71, 84, 103, 0.4)' : '1px solid rgba(234, 236, 240, 1)',
              paddingBottom: '12px',
              marginBottom: '12px',
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                backgroundColor: SLACK_PURPLE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SlackIcon size={22} />
            </Box>
            <Typography
              sx={{
                color: titleColor,
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: 1.3,
              }}
            >
              Slack
            </Typography>
          </Box>
          <Typography
            sx={{
              color: subtitleColor,
              fontSize: '12px',
              lineHeight: 1.45,
              marginBottom: '12px',
            }}
          >
            Connect your Slack workspace to pull messages and channels, or push messages from your workflows.
          </Typography>
          <Button
            variant="contained"
            size="small"
            disabled={isConnecting}
            onClick={onClick}
            sx={{
              backgroundColor: SLACK_PURPLE,
              color: '#fff',
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 600,
              borderRadius: '6px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#611f69',
                boxShadow: 'none',
              },
            }}
          >
            {isConnecting ? 'Connecting...' : 'Connect to Slack'}
          </Button>
        </Box>
      )}
    </ConnectToProvider>
  );
};

export default SlackConnectionCard;
